import { z } from 'zod';

export interface Program {
  programId: string;
  programName: string;
  jobFamily: string;
  description: string;
  modules: Module[];
}

export interface Module {
  moduleId: string;
  moduleName: string;
  courseCode?: string;
  description?: string;
  beelajarId?: string | null;
}

export const assessmentScheduleSchema = z.object({
  name: z.string().min(1, 'Schedule name is required'),
  type: z.string().min(1, 'Assessment type is required'),
  programId: z.string().optional(),
  learningObjectives: z.string().optional(),
  description: z.string(),
  isWithQuestion: z.enum(['Y', 'N']),
  startTime: z.date(),
  endTime: z.date(),
  notificationStatus: z.string().default('pending'),
  notes: z.string().optional(),
  statusKurikulum: z.boolean(),
  isBeforeSchedule: z.boolean(),
  previousScheduleId: z.string().optional().nullable(),
  notificationSchedule: z.date(),
  source: z.string().optional(),
  typeAssessment: z.enum(['Team', 'Individu']).optional(),
  timeAssessment: z.enum(['before', 'during', 'after', 'gap']).optional(),
  isRandom: z.enum(['Y', 'N']).optional(),
  isReset: z.enum(['Y', 'N', 'R']).optional(),
  courseSource: z.enum(['LCMS', 'Beelajar']).default('LCMS'),
  areaID: z.string().min(1, 'Area is required')
});

export type AssessmentScheduleFormData = z.infer<typeof assessmentScheduleSchema>;

export interface Participant {
  binusianId: string;
  name: string;
  division?: string;
  type?: string;
  photoUrl?: string;
  email?: string;
  participantId?: string;
  participants?: any[];
  isSelfAssess?: boolean;
  courseId?: string;
  courseName?: string;
  listModule?: string[];
}

export interface RangeItem {
  label: string;
  value: string;
  sequence?: number;
  trAssessmentQuestionRangeItemID?: string;
  msQuestionRangeItemID?: string;
}

export interface Question {
  id: string;
  title: string;
  description?: string;
  type: 'Choose One' | 'Range' | 'Free-text';
  moduleId: string;
  moduleName?: string;
  groupId?: string;
  groupName?: string;
  respondentType: string;
  sequence?: number;
  rangeItems?: { label: string; value: string; sequence?: number }[];
  externalTypeIds?: string[];
  externalTypeName?: any;
  typeLabel?: any;
  msExternalQuestionTypeID?: string;
  category?: string;
  skalaCategory?: string;
}

export interface ImportedQuestion {
  id: string;
  title: string;
  category?: string;
  skalaCategory?: string;
  description?: string;
  type: 'Choose One' | 'Range' | 'Free-text';
  moduleId: string;
  moduleName?: string;
  respondentType: string;
  rangeItems?: { label: string; value: string }[];
  isValid: boolean;
  errors: string[];
}

export interface QuestionTemplate {
  Question: string;
  Description?: string;
  Type: 'Choose One' | 'Range' | 'Free-text';
  Category?: string;
  SkalaCategory?: string;
  RangeItems?: string;
}

export const editAssessmentScheduleSchema = z
  .object({
    name: z.string().min(1, 'Assessment schedule name is required'),
    type: z.enum(['product', 'service'], {
      required_error: 'Type is required'
    }),
    programID: z.string().min(1, 'Program is required'),
    learningObjectives: z.string().optional(),
    description: z.string().optional(),
    startTime: z.date({
      required_error: 'Start time is required'
    }),
    endTime: z.date({
      required_error: 'End time is required'
    }),
    typeAssessment: z.enum(['Team', 'Individu'], {
      required_error: 'Assessment team type is required'
    }),
    isWithQuestion: z.enum(['Y', 'N'], {
      required_error: 'With question option is required'
    }),
    timeAssessment: z.enum(['before', 'after'], {
      required_error: 'Time assessment is required'
    }),
    isRandom: z.enum(['Y', 'N'], {
      required_error: 'Random order option is required'
    }),
    notificationSchedule: z.date().optional(),
    previousScheduleID: z.string().optional(),
    notes: z.string().optional()
  })
  .refine((data) => data.endTime > data.startTime, {
    message: 'End time must be after start time',
    path: ['endTime']
  });
